import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CompanyModel } from '@data-layer/models/companies';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { forkJoin, Observable, ReplaySubject } from 'rxjs';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { BondModel, BondRequestModel, ShipmentModel } from '@data-layer/models/common';
import { EntityBondComponent } from '@shared/entity/components';
import { FormHelper } from '@helpers/form.helper';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntrySummaryState, enumEntrySummarySubmissionType, enumEntrySummaryType } from '@data-layer/enums/entry-summaries';
import { enumBondType } from '@data-layer/enums/common';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { DESC_ENTITY } from '@constants/desc-entity';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { ActivatedRoute, Router } from '@angular/router';
import { CHINA_COUNTRY_CODE, ENTERED_VALUE_CHINA_LIMIT, ENTERED_VALUE_TOTAL_LIMIT } from '@constants/constants';
import {
  FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP,
  INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP,
  INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP,
} from '@constants/entity-validation-messages';
import { isCbpAssignedNumberImporter } from '@helpers/importer.helper';
import { EntrySummaryService } from '@services/entry-summary.service';
import { flatten, omit } from 'lodash-es';

@Component({
  selector: 'es-shipment-details-form-desktop',
  template: `
    <p class="mb-0">{{ 'LABELS.CHOOSE_TYPE_OF_SHIPMENT_ES' | translate }}</p>
    <p class="text-hint">{{ 'LABELS.CHOOSE_TYPE_OF_SHIPMENT_ES_HINT' | translate : { value: enteredValue } }}</p>
    <p class="text-hint" *ngIf="isAniImporter">
      {{ 'LABELS.FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER_HINT' | translate }}
    </p>

    <form [formGroup]="formGroup" autocomplete="off">
      <div class="d-flex justify-content-between shipment-types">
        <div
          class="card card-step shipment-type"
          [ngClass]="{
            'is-selected': typeControl.value === enumEntrySummaryType.FORMAL,
            'is-full-width': isOnlyFormalTypeAvailable
          }"
        >
          <div class="card-header">
            <app-radio-button
              [tooltipData]="shipmentTypeTooltip | translate"
              [control]="typeControl"
              [id]="isSidebar ? 'sidebar-shipment-type-formal' : 'shipment-type-formal'"
              [label]="'FORM_LABEL.FORMAL'"
              [value]="enumEntrySummaryType.FORMAL"
              [name]="'shipment-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3" *ngIf="isFormalThresholdHintVisible">
            <span [innerHTML]="'FORM_HELPER.FORMAL_DESKTOP' | translate"></span>
          </div>
        </div>
        <div
          class="card card-step shipment-type"
          *ngIf="isInformalTypeVisible"
          [ngClass]="{ 'is-selected': typeControl.value === enumEntrySummaryType.INFORMAL }"
        >
          <div class="card-header">
            <app-radio-button
              [tooltipData]="shipmentTypeTooltip | translate"
              [control]="typeControl"
              [id]="isSidebar ? 'sidebar-shipment-type-informal' : 'shipment-type-informal'"
              [label]="'FORM_LABEL.INFORMAL'"
              [value]="enumEntrySummaryType.INFORMAL"
              [name]="'shipment-type'"
              [disabled]="isAniImporter"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span [innerHTML]="'FORM_HELPER.INFORMAL_DESKTOP' | translate"></span>
          </div>
        </div>
      </div>
    </form>

    <div class="mt-4" *ngIf="typeControl.value === enumEntrySummaryType.FORMAL">
      <div class="shipment-type-info">
        <p>{{ 'LABELS.CUSTOMS_BOND' | translate }}</p>
        <span [innerHTML]="'INFO.BN.SUB_TITLE' | translate"></span>
      </div>

      <app-entity-bond
        [referenceNumber]="referenceNumber"
        [errors]="errors"
        [showSuretyCodeForContinuousBond]="true"
        [bondRequest]="bondRequest"
        [formGroup]="bondFormGroup"
        [purchaseBondForm]="bondRequestForm"
        [importerIdentifierQual]="importer?.entityIdentifierQualifier"
        [importerIdentifier]="importer?.entityIdentifier"
        [isEntrySummary]="true"
        [isShowOnlyActive]="true"
        [isDesktop]="true"
        [isManagement]="isManagement"
        [canEdit]="isCanEdit"
      ></app-entity-bond>
    </div>

    <div class="price-notifications mt-4" *ngIf="!!sumValidation">
      <div class="price-notifications-icon">
        <span class="icon-ic_big_v"></span>
      </div>
      <div class="price-notifications-text">
        <span [innerHTML]="'BACK_ERROR.' + sumValidation | translate : { total: enteredValue }"></span>
      </div>
    </div>

    <missing-fields [code]="'MESSAGE_CODE.ENTRY_SUMMARY'" (confirm)="onSubmit(false)" (decline)="decline()"></missing-fields>
  `,
  styleUrls: ['./es-shipment-details-form-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsShipmentDetailsFormDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public company: CompanyModel;
  @Input() public isSidebar: boolean;
  // Additive (broker review): force editability on state 50 (ActionsHelper.isCanEdit excludes it) and
  // put the bond component in management mode (SEB-only, no company catalog). Default false → user-side
  // unchanged. #13102.
  @Input() public forceEditable: boolean = false;
  @Input() public isManagement: boolean = false;
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();

  public typeControl: UntypedFormControl;
  public bondFormGroup: UntypedFormGroup;
  public bondRequestForm: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm };
  public bond: BondModel;
  public bondRequest: BondRequestModel;

  public info = DESC_ENTITY.BN;
  public returnUrl: string;
  public isCanEdit: boolean = false;
  public importer: EntityModel;

  @ViewChild(EntityBondComponent, { static: false })
  public entityBondComponent: EntityBondComponent;
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public enumEntrySummaryType = enumEntrySummaryType;

  public enteredValue: number;

  private referenceNumber: string;
  private esId: number;
  private firstBlId: number;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService,
    private router: Router,
    private entrySummaryStateService: EntrySummaryStateService,
    private activeRoute: ActivatedRoute,
    private fh: FormHelper,
    private entrySummaryService: EntrySummaryService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.returnUrl = this.activeRoute.snapshot.queryParams.returnUrl;
    this.createErrors();
    this.bondFormGroup = this.fh.createEntrySummaryBondForm();

    this.typeControl = this.formGroup.get('type') as UntypedFormControl;

    const isValidateAfterInit = this.activeRoute.snapshot.queryParams.validateAfterInit
      ? JSON.parse(this.activeRoute.snapshot.queryParams.validateAfterInit)
      : false;
    if (isValidateAfterInit) {
      this.fh.markAsTouched(this.bondFormGroup);
    }

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.destroy$)).subscribe((entrySummary) => {
      if (entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED) {
        this.bondRequestForm = this.fh.createPurchaseBondForm();
      } else {
        this.bondRequestForm = undefined;
      }
      this.isCanEdit = this.forceEditable || EntrySummaryActionsHelper.isCanEdit(entrySummary);
      this.importer = this.getImporter(entrySummary);

      this.referenceNumber = entrySummary.referenceNumber;
      this.esId = entrySummary.id;
      this.firstBlId = entrySummary && entrySummary.shipments && entrySummary.shipments.length ? entrySummary.shipments[0].id : null;
      this.patchData(isValidateAfterInit);
      // OnPush: `importer` drives the entry-type presentation (#13284) and the bond identifier bindings,
      // and it is assigned outside a template event, so the view has to be marked explicitly.
      this.cdr.markForCheck();
    });

    this.typeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.entrySummary.type = val;

      this.getEnteredValue();
    });

    this.getEnteredValue();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.entrySummary) {
      this.getEnteredValue();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get isFormal(): boolean {
    return this.typeControl.value === enumEntrySummaryType.FORMAL;
  }

  public get isAniImporter(): boolean {
    return isCbpAssignedNumberImporter(this.importer);
  }

  /**
   * #13284: Informal is not a valid choice for an importer with a CBP Assigned Number, so the option is
   * hidden. It stays visible (disabled) while it is the value the filing actually carries, so a legacy or
   * already-submitted Informal entry still shows its real type instead of an empty selection.
   */
  public get isInformalTypeVisible(): boolean {
    return !this.isAniImporter || this.typeControl.value === enumEntrySummaryType.INFORMAL;
  }

  /** Formal spans the whole row when it is the only card rendered (#13284). */
  public get isOnlyFormalTypeAvailable(): boolean {
    return !this.isInformalTypeVisible;
  }

  /** #13346: the $250 threshold is moot when Formal is the only card left, so the copy is dropped there. */
  public get isFormalThresholdHintVisible(): boolean {
    return this.isInformalTypeVisible;
  }

  public get shipmentTypeTooltip(): string {
    return this.isAniImporter ? 'LABELS.FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER_HINT' : 'TOOLTIP.SHIPMENT_TYPE_TOOLTIP';
  }

  public continue(): void {
    if (!this.isCanEdit) {
      return;
    }

    if (this.isFormal) {
      (this.entityBondComponent.isSelectedPurchase
        ? this.entrySummaryStateService.singleBondRequest(this.bondRequestForm.get('portCode').value)
        : this.updateBond()
      )
        .pipe(
          switchMap(() => {
            return this.entrySummaryStateService.loadEntrySummary(this.referenceNumber);
          }),
          take(1),
        )
        .subscribe((entrySummary: EntrySummaryModel) => {
          this.entrySummaryStateService.updateCurrentEntrySummary(entrySummary);
          this.patchData(false);

          this.dataSaved.emit();
        });
    } else {
      this.entrySummaryStateService
        .updateBond({ suretyCode: null, type: null, bondAmount: null, referenceNumber: null })
        .pipe(
          tap(() =>
            this.entrySummaryStateService.updateCurrentEntrySummary({
              ...this.entrySummary,
              bondInfo: null,
              bondRequest: null,
            }),
          ),
          switchMap(() =>
            this.entrySummaryStateService.updateEntrySummary({
              ...this.entrySummary,
              type: enumEntrySummaryType.INFORMAL,
            }),
          ),
          take(1),
        )
        .subscribe((entrySummary: EntrySummaryModel) => {
          this.entrySummaryStateService.updateCurrentEntrySummary(entrySummary);
          this.dataSaved.emit();
        });
    }
  }

  public onSubmit(needValidate: boolean = true) {
    if (!this.isCanEdit) {
      return;
    }
    const invalidFields =
      this.isFormal && !!this.entityBondComponent
        ? this.fh.validateAllFields(this.entityBondComponent.isSelectedPurchase ? this.bondRequestForm : this.bondFormGroup)
        : [];
    if (needValidate && Object.keys(invalidFields).length) {
      if (this.bondFormGroup.get('type').value === enumBondType.CONTINUOUS) {
        delete invalidFields['referenceNumber'];
      }
      if (Object.keys(invalidFields).length) {
        this.openModal(invalidFields);
        return;
      }
    }

    this.continue();
  }

  public decline(): void {
    this.onSubmit(false);
  }

  public get sumValidation(): string {
    let result = null;

    const allLineItems: LineModel[] = this.getAllLines();
    const isHasChina: boolean = allLineItems.map((line) => line.countryOfOrigin?.code).some((code) => code === CHINA_COUNTRY_CODE);
    const limit: number = isHasChina ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
    // #13284: no "switch to Informal" banner for an ANI importer — Informal is not an option for it.
    if (this.entrySummary.type === enumEntrySummaryType.FORMAL && !this.isAniImporter && this.enteredValue <= limit) {
      result = FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP;
    }
    if (this.entrySummary.type === enumEntrySummaryType.INFORMAL && this.enteredValue > limit) {
      const isNonDraft = this.entrySummary.state !== enumEntrySummaryState.NOT_SENT;
      result = isNonDraft
        ? INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP
        : INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP;
    }

    return result;
  }

  private openModal(data) {
    this.missingFieldModal.openCustomModal(data);
  }

  private patchData(isValidateAfterInit: boolean) {
    if (this.isCanEdit) {
      this.bondFormGroup.enable();
    } else {
      this.bondFormGroup.disable();
    }
    forkJoin({
      bond: this.entrySummaryStateService.getBond().pipe(take(1)),
      bondRequest: this.entrySummaryStateService.getBondRequest().pipe(take(1)),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ bond, bondRequest }) => {
        this.bond = !!bond ? new BondModel(omit(bond, ['validationMessages'])) : new BondModel();
        this.bondRequest = bondRequest;

        const bondAmount = this.bond.bondAmount || 50000;

        this.bondFormGroup.patchValue({
          ...this.bond,
          bondAmount,
        });

        if (this.bondRequestForm && this.bondRequest) {
          this.bondRequestForm.patchValue(this.bondRequest);
        }
      });
  }

  private updateBond(): Observable<any> {
    const { suretyCode: formSuretyCode, referenceNumber: formReferenceNumber, bondAmount, type } = this.bondFormGroup.value;
    const updatingBond = {
      ...this.bond,
      suretyCode: formSuretyCode,
      referenceNumber: formReferenceNumber,
      type,
      bondAmount,
    };
    return this.entrySummaryStateService.updateBond(updatingBond);
  }

  private createErrors() {
    this.errors = {
      suretyCode: {
        required: 'FORM_ERROR.REQUIRED',
        wrongSuretyCode: 'FORM_ERROR.WRONG_SURETY_CODE',
      },
      referenceNumber: {
        required: 'FORM_ERROR.REQUIRED',
        wrongReferenceNumber: 'FORM_ERROR.WRONG_REFERENCE_NUMBER',
        shouldHasAtLeastOneLetter: 'FORM_ERROR.SHOULD_HAS_AT_LEAST_ONE_LETTER',
      },
    };
  }

  private getImporter(entrySummary: EntrySummaryModel) {
    return entrySummary?.importer ? entrySummary?.importer : null;
  }

  private getAllLines(): LineModel[] {
    const allShipments: ShipmentModel[] = this.entrySummary?.shipments || [];
    const allInvoices: InvoiceModel[] = flatten(allShipments.map((shipment) => shipment.invoices || []));
    return flatten(allInvoices.map((invoice) => invoice.lines || []));
  }

  private getEnteredValue(): void {
    this.entrySummaryService
      .getEntrySummaryEnteredValue(this.entrySummary)
      .pipe(take(1))
      .subscribe((value) => {
        this.enteredValue = value;
      });
  }
}
